import { useEffect, useMemo } from 'react';

import { IWorldSettings } from '@interfaces/worlds';
import { IUser } from '@interfaces/user';
import {
  POLLY_TO_LANGUAGE_CODE,
  PENDING_LANGUAGE_STORAGE_KEY,
  PENDING_LANGUAGE_TTL_MS,
} from '@constants/language';
import { getAiAssistantLanguageOptions } from '@helpers/assistant';
import { MytaverseLogger } from '@helpers/logger';

interface IUseInitialLanguageParams {
  user: IUser | undefined;
  appSettings: IWorldSettings | null;
  selectLanguage: (language: string) => void;
}

export const useInitialLanguage = ({
  user,
  appSettings,
  selectLanguage,
}: IUseInitialLanguageParams): void => {
  const languageOptions = useMemo(
    () => getAiAssistantLanguageOptions(appSettings?.aiAssistant),
    [appSettings?.aiAssistant],
  );

  useEffect(() => {
    if (!languageOptions?.length || !user?.id) {
      return;
    }

    const rawPending = localStorage.getItem(PENDING_LANGUAGE_STORAGE_KEY);

    if (rawPending) {
      localStorage.removeItem(PENDING_LANGUAGE_STORAGE_KEY);

      let pendingLanguage: string | undefined;

      try {
        const parsed = JSON.parse(rawPending) as {
          language?: string;
          savedAt?: number;
        };
        const isFresh =
          typeof parsed?.savedAt === 'number' &&
          Date.now() - parsed.savedAt <= PENDING_LANGUAGE_TTL_MS;

        if (isFresh && parsed?.language) {
          pendingLanguage = parsed.language;
        }
      } catch {
        // Malformed marker — ignore and fall through to the normal chain.
      }

      let pendingMatch: { key: string; label: string } | undefined;
      if (pendingLanguage) {
        const wantedLanguage = pendingLanguage;
        pendingMatch = languageOptions.find(
          ({ key }) => key.toLowerCase() === wantedLanguage.toLowerCase(),
        );
      }

      if (pendingMatch) {
        MytaverseLogger.log(`language pendingMatch: ${pendingMatch.label}`);
        selectLanguage(pendingMatch.key);
        return;
      }
    }

    let profileMatch: { key: string; label: string } | undefined;
    if (user?.profileLanguage) {
      const wantedLanguage = user.profileLanguage;
      profileMatch = languageOptions.find(
        ({ key }) => key.toLowerCase() === wantedLanguage.toLowerCase(),
      );
    }

    if (profileMatch) {
      MytaverseLogger.log(`language profileMatch: ${profileMatch.label}`);
      selectLanguage(profileMatch.key);
      return;
    }

    const storedLanguage = localStorage.getItem('language');
    const storedMatch = storedLanguage
      ? languageOptions.find(
          ({ key }) => key.toLowerCase() === storedLanguage.toLowerCase(),
        )
      : undefined;

    if (storedMatch) {
      MytaverseLogger.log(
        `language storedMatch: ${JSON.stringify(storedMatch)}`,
      );
      selectLanguage(storedMatch.key);
      return;
    }

    const browserLanguages = [...(navigator.languages ?? [navigator.language])];

    let browserMatch: string | undefined;

    for (const browserLang of browserLanguages) {
      const lower = browserLang.toLowerCase();
      const primaryTag = lower.split('-')[0];
      const found = languageOptions.find(
        ({ key }) =>
          key.toLowerCase() === lower || key.toLowerCase() === primaryTag,
      );

      if (found) {
        browserMatch = found.key;
        break;
      }
    }

    if (browserMatch) {
      MytaverseLogger.log(`language browserMatch: ${browserMatch}`);
      selectLanguage(browserMatch);
      return;
    }

    const base = appSettings?.aiAssistant?.baseLanguage;

    if (base) {
      MytaverseLogger.log(`language base: ${base}`);
      selectLanguage(POLLY_TO_LANGUAGE_CODE[base] ?? base);
    }
  }, [
    languageOptions,
    appSettings?.aiAssistant?.baseLanguage,
    selectLanguage,
    user?.id,
  ]);
};
